import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';

/**
 * A basic searchable select component based on MUI Autocomplete.
 */
export const SearchableSelectBase = ({
    options,
    value,
    onChange,
    label,
    placeholder,
    loading = false,
    disabled = false,
    noOptionsText = "Nenhum item encontrado",
    error = false,
    helperText = null,
    getOptionLabel = (option) => {
        if (!option) return '';
        if (typeof option === 'string') return option;
        return option.label || option.nome || option.name || String(option);
    },
    isOptionEqualToValue = (option, value) => {
        if (!option || !value) return false;
        const optVal = typeof option === 'string' ? option : (option.id || option.value || option.nome || option.name);
        const valVal = typeof value === 'string' ? value : (value.id || value.value || value.nome || value.name);
        return optVal === valVal;
    },
    size = "small",
    sx = {},
    multiple = false, // Added multiple prop
    required = false, // Added required prop to be passed to TextField
    freeSolo = false
}) => {
    // Determine the value to pass to Autocomplete
    let autocompleteValue = null;

    if (multiple) {
        autocompleteValue = Array.isArray(value) ? value.map(val => {
            const found = options.find(opt => {
                if (typeof opt === 'string') return opt === val;
                const optVal = opt.id || opt.value || opt.nome || opt.name;
                const valVal = (val && typeof val === 'object') ? (val.id || val.value || val.nome || val.name) : val;
                return optVal === valVal;
            });
            return found || val;
        }) : [];
    } else {
        autocompleteValue = options.find(opt => {
            if (typeof opt === 'string') return opt === value;
            const optVal = opt.id || opt.value || opt.nome || opt.name;
            const valVal = (value && typeof value === 'object') ? (value.id || value.value || value.nome || value.name) : value;
            return optVal === valVal;
        }) || (freeSolo && value ? value : null);
    }

    return (
        <Autocomplete
            fullWidth
            multiple={multiple} // Pass multiple prop to MUI Autocomplete
            freeSolo={freeSolo}
            options={options}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={autocompleteValue}
            disabled={disabled}
            loading={loading}
            noOptionsText={noOptionsText}
            onChange={(_, newValue) => {
                if (multiple) {
                    const vals = Array.isArray(newValue) ? newValue.map(v =>
                        typeof v === 'string' ? v : (v.id || v.value || v.nome || v.name)
                    ) : [];
                    onChange(vals);
                } else {
                    const val = newValue ? (typeof newValue === 'string' ? newValue : (newValue.id || newValue.value || newValue.nome || newValue.name)) : '';
                    onChange(val);
                }
            }}
            onInputChange={(_, newInputValue, reason) => {
                if (freeSolo && !multiple && reason === 'input') {
                    onChange(newInputValue);
                }
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    size={size}
                    required={required}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            '& fieldset': {
                                borderColor: '#e2e8f0',
                            },
                            '&:hover fieldset': {
                                borderColor: '#cbd5e1',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#3b82f6',
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: '#64748b',
                            '&.Mui-focused': {
                                color: '#3b82f6',
                            },
                        },
                        ...sx
                    }}
                />
            )}
            componentsProps={{
                paper: {
                    sx: {
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e2e8f0',
                        marginTop: '4px',
                    }
                }
            }}
            slotProps={{
                popper: {
                    sx: {
                        zIndex: 99999
                    }
                }
            }}
        />
    );
};

/**
 * RHF integrated version
 */
const SearchableSelect = ({ name, control, rules, defaultValue = '', ...props }) => {
    if (!control) {
        // Fallback to basic if no control provided (useful for manual integration)
        return <SearchableSelectBase {...props} />;
    }

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={rules}
            render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                <SearchableSelectBase
                    {...props}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                />
            )}
        />
    );
};

export default SearchableSelect;
